import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import AxisPicker from "./AxisPicker";
import ColorScalePicker from "./ColorScalePicker";
import LabelsDialog from "./LabelsDialog";
import { regionKey, useLidarClearance } from "./LidarClearanceContext";
import ProjectPicker from "./ProjectPicker";
import {
  createSystemAxisLink,
  deleteSystemAxisLink,
  updateSystemAxisLink,
  updateSystem as updateSystemRequest,
} from "./api";
import { colorScaleGapWarning } from "./colorScaleDisplay";
import type { LidarClearanceSystem } from "./schemas";
import t from "./translations";

type Props = {
  system: LidarClearanceSystem | null;
  onClose: () => void;
};

export default function SystemDetailsDrawer({ system, onClose }: Props) {
  const { axes, systemAxisLinksBySystem, colorScaleByKey, refetch } =
    useLidarClearance();

  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [savingProperties, setSavingProperties] = useState(false);
  const [newAxisId, setNewAxisId] = useState<number | null>(null);
  const [addingAxis, setAddingAxis] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);

  useEffect(() => {
    setName(system?.name ?? "");
    setCustomerId(system?.customer_id ?? null);
    setNewAxisId(null);
  }, [system]);

  const links = system
    ? systemAxisLinksBySystem.get(regionKey(system.region, system.id)) ?? []
    : [];
  const linkedAxisIds = useMemo(
    () => new Set(links.map((entry) => entry.axis.id)),
    [links]
  );
  const linkedAxes = useMemo(() => links.map((entry) => entry.axis), [links]);

  if (!system) return null;

  const propertiesDirty =
    name.trim() !== system.name || customerId !== system.customer_id;
  const canSaveProperties =
    propertiesDirty && !savingProperties && name.trim().length > 0;

  const saveProperties = async () => {
    setSavingProperties(true);
    try {
      await updateSystemRequest(system.region, system.id, {
        name: name.trim(),
        customer_id: customerId,
      });
      await refetch();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.updateSystem" });
      toast.error(t.FailedToUpdateSystem);
    } finally {
      setSavingProperties(false);
    }
  };

  const addAxis = async () => {
    if (newAxisId === null) return;
    const axis = axes.find(
      (candidate) =>
        candidate.id === newAxisId && candidate.region === system.region
    );
    if (!axis) return;
    setAddingAxis(true);
    try {
      // apps/api never falls back to the axis's own color_scale, so the link
      // must always carry a concrete value rather than null.
      await createSystemAxisLink(
        system.region,
        system.id,
        newAxisId,
        axis.color_scale
      );
      await refetch();
      setNewAxisId(null);
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.linkAxis" });
      toast.error(t.FailedToLinkAxis);
    } finally {
      setAddingAxis(false);
    }
  };

  const changeLinkColorScale = async (linkId: number, value: number) => {
    try {
      await updateSystemAxisLink(system.region, system.id, linkId, value);
      await refetch();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.updateAxisLink" });
      toast.error(t.FailedToUpdateAxisLink);
    }
  };

  const removeLink = async (linkId: number) => {
    try {
      await deleteSystemAxisLink(system.region, system.id, linkId);
      await refetch();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.unlinkAxis" });
      toast.error(t.FailedToUnlinkAxis);
    }
  };

  return (
    <Drawer anchor="right" open={system !== null} onClose={onClose}>
      <Box sx={{ width: 460, p: 3 }} data-testid="system-details-drawer">
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <TextField
              label={t.NameLabel}
              value={name}
              onChange={(event) => setName(event.target.value)}
              sx={{ flexGrow: 1 }}
              slotProps={{ htmlInput: { "data-testid": "system-drawer.name" } }}
            />
            <Tooltip title={t.SaveButton}>
              <span>
                <IconButton
                  color="primary"
                  disabled={!canSaveProperties}
                  onClick={saveProperties}
                  data-testid="system-drawer.save"
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <ProjectPicker
            region={system.region}
            value={customerId}
            onChange={setCustomerId}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>
          {t.LinkedAxesTitle}
        </Typography>
        {links.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t.NoAxesLinked}
          </Typography>
        ) : (
          <Stack>
            {links.map((entry, index) => {
              const effectiveScaleId =
                entry.link.color_scale ?? entry.axis.color_scale;
              const scale = colorScaleByKey.get(
                regionKey(system.region, effectiveScaleId)
              );
              const gapWarning = colorScaleGapWarning(
                entry.axis,
                scale,
                t.ColorScaleGapWarning
              );

              return (
                <Box
                  key={entry.link.id}
                  sx={{
                    px: 1,
                    py: 1,
                    backgroundColor:
                      index % 2 === 0 ? "background.paper" : "grey.100",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ flexGrow: 1, minWidth: 0, wordBreak: "break-word" }}
                    >
                      {entry.axis.name_en}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>
                      <ColorScalePicker
                        region={system.region}
                        min={entry.axis.min_value}
                        max={entry.axis.max_value}
                        value={entry.link.color_scale}
                        onChange={(value) =>
                          changeLinkColorScale(
                            entry.link.id,
                            value ?? entry.axis.color_scale
                          )
                        }
                        defaultOption={{
                          label: t.ColorScaleDefaultOption,
                          scaleId: entry.axis.color_scale,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        width: 28,
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {gapWarning && (
                        <Tooltip title={gapWarning}>
                          <CancelIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      aria-label={t.RemoveAxisLink}
                      onClick={() => removeLink(entry.link.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: "center" }}>
          <Box sx={{ flexGrow: 1 }}>
            <AxisPicker
              region={system.region}
              excludeAxisIds={linkedAxisIds}
              value={newAxisId}
              onChange={setNewAxisId}
            />
          </Box>
          <Button
            onClick={addAxis}
            disabled={newAxisId === null || addingAxis}
            variant="outlined"
          >
            {t.AddAxisButton}
          </Button>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Button
          variant="outlined"
          onClick={() => setLabelsOpen(true)}
          data-testid="system-drawer.open-labels"
        >
          {t.OpenLabelsButton}
        </Button>
        <LabelsDialog
          open={labelsOpen}
          system={system}
          axes={linkedAxes}
          onClose={() => setLabelsOpen(false)}
        />
      </Box>
    </Drawer>
  );
}
